import type { ApiV3 } from '@teamworksdev/influencer-core';
import { useSchoolsService } from '@/api/schoolsService';
import { useUserStore } from '@/stores/userStore';
import { sortBy } from 'lodash-es';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { CanceledError } from 'axios';

export const useSchoolsStore = defineStore('schools', () => {
	const schoolsRaw = ref<ApiV3.School[]>();
	const isFetchingSchools = ref(false);
	const fetchPromise = ref<Promise<void> | null>(null);
	const userStore = useUserStore();

	const schools = computed<ApiV3.School[]>(() => sortBy(schoolsRaw.value, 'name'));

	const fetchSchools = async () => {
		if (schoolsRaw.value) return;
		if (fetchPromise.value) return fetchPromise.value;

		isFetchingSchools.value = true;

		fetchPromise.value = (async () => {
			try {
				schoolsRaw.value = await useSchoolsService().getSchools({ all: 1 });
			} catch (error) {
				if (error instanceof CanceledError) return;
				console.error(error);
			} finally {
				isFetchingSchools.value = false;
				fetchPromise.value = null;
			}
		})();

		return fetchPromise.value;
	};

	const $reset = () => {
		schoolsRaw.value = undefined;
		isFetchingSchools.value = false;
		fetchPromise.value = null;
	};

	watch(() => userStore.profile, (val) => {
		if (!val) return;
		$reset();
	}, { immediate: true });

	return {
		schools,
		isFetchingSchools,
		fetchSchools,
		$reset,
	};
});
